import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonHelper } from '@shared/helpers/common.helper';
import { ACTIVE_USER } from '@modules/auth/providers/auth.provider';
import { BehaviorSubject, catchError, EMPTY, finalize, first, Observable, switchMap, tap } from 'rxjs';
import { User } from '@modules/user/models/user.model';
import { CommonModule } from '@angular/common';
import { CompanyDirector } from '@modules/company/models/company-director.model';
import { LetModule } from '@ngrx/component';
import { Corporate } from '@modules/company/models/corporate.model';
import { MatDividerModule } from '@angular/material/divider';
import { OfficerProfile } from '@modules/officer/models/officer-profile.model';
import { OfficerCompany } from '@modules/officer/models/officer-company.model';
import { OfficerPerson } from '@modules/officer/models/officer-person.model';
import { CompanyIndirectShareholder } from "@modules/company/models/company-indirect-shareholder.model";
import { CompanyEventService } from "@modules/company/services/company-event.service";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import {
  OfficerResolutionRequestDialogComponent
} from "@shared/component/company/corporate-request/officer-resolution/officer-resolution-reuqest-dialog/officer-resolution-request-dialog.component";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
  CountrySelectInputComponent
} from "@shared/component/common/country-select-input/country-select-input.component";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatOptionModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { OfficerIdentificationTypeType, OfficerTypeEnum } from "@generated/graphql";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import {
  OfficerTypeProp
} from "@modules/company/components/company-indirect-shareholder-dialog/company-indirect-shareholder-dialog.component";
import { PercentInputComponent } from "@shared/component/percent-input/percent-input.component";
import { ShareDirectivesModule } from "@shared/share-directives/share-directives.module";
import { FormFactory } from "@shared/factories/form.factory";
import { OfficerService } from "@modules/officer/services/officer.service";
import { startWith } from "rxjs/operators";
import { Validators } from "@config/validators.config";
import { OfficerIndirectCompany } from "@modules/officer/models/officer-indirect-company.model";
import { OfficerIndirectPerson } from "@modules/officer/models/officer-indirect-person.model";
import { CompanyIndirectShareholderApi } from "@modules/company/api/company-indirect-shareholder.api";
import {
  CompanyIndirectCompanyShareholderInput,
  CompanyIndirectIndividualShareholderInput,
  UpdateCompanyIndirectCompanyShareholderInput,
  UpdateCompanyIndirectIndividualShareholderInput
} from "@generated/graphql";
import { SnackBarService } from "@shared/services/snack-bar.service";
import { CompanyShareholder } from '@app/modules/company/models/company-shareholder.model';

export type OfficerInformationItem = Corporate | CompanyDirector;

@Component({
  selector: 'app-officer-information',
  standalone: true,
  templateUrl: './officer-information.component.html',
  styleUrls: ['./officer-information.component.scss'],
  imports: [CommonModule, LetModule, MatDividerModule, MatIconModule, MatButtonModule, MatTooltipModule,
    CountrySelectInputComponent, MatDatepickerModule, MatFormFieldModule, MatInputModule,
    MatOptionModule, MatSelectModule, PercentInputComponent, MatDialogModule, ShareDirectivesModule,
    ReactiveFormsModule],
})
export class OfficerInformationComponent implements OnInit {
  @Input() officerInfo: OfficerInformationItem;
  @Input() allowEdit = true;
  @Input() remainingPercentageOfShares?: number
  @Output() officerUpdated: EventEmitter<OfficerInformationItem> = new EventEmitter();
  today: Date = new Date();
  identificationType: OfficerIdentificationTypeType[] = [];
  control = new FormControl<OfficerTypeProp>({
    value: this.item ? this.item.officer.officerType as OfficerTypeProp : OfficerTypeEnum.Corporate,
    disabled: !!this.item
  })
  companyIndirectCompanyShareholderForm = this.formFactory.companyIndirectCompanyShareholderForm()
  companyIndirectIndividualShareholderForm = this.formFactory.companyIndirectIndividualShareholderForm()
  form: ReturnType<FormFactory['companyIndirectCompanyShareholderForm']> | ReturnType<FormFactory['companyIndirectIndividualShareholderForm']>

  private readonly _loading$ = new BehaviorSubject(false);
  isVisible: boolean = false;

  toggleEditShareholder() {
    this.init();
    this.isVisible = !this.isVisible;
  }

  boxStyles = {
    'border': '1px solid rgba(0, 0, 0, 0.12)',
    'border-radius': '4px'
  };

  get item() {
    return this.officerInfo;
  }

  get isDirector() {
    return this.officerInfo instanceof CompanyDirector;
  }

  get directorOfficer() {
    return this.isDirector ? (this.officerInfo as CompanyDirector) : null;
  }

  get loading$() {
    return this._loading$.asObservable()
  }

  get percentageOfShares(): number {
    if ((this.item instanceof CompanyIndirectShareholder) || (this.item instanceof CompanyShareholder)) {
      return Number((this.item.percentageOfShares * 100).toFixed(2));
    }

    return 0
  }

  constructor(
    @Inject(ACTIVE_USER) protected readonly activeUser$: Observable<User>,
    protected readonly companyEventService: CompanyEventService,
    protected readonly matDialog: MatDialog,
    protected readonly commonHelper: CommonHelper,
    private readonly formFactory: FormFactory,
    private officerService: OfficerService,
    private readonly companyIndirectShareholderApi: CompanyIndirectShareholderApi,
    private readonly snackBarService: SnackBarService,
    private readonly cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    this.officerService.getIdentificationType().subscribe((data) => {
      this.identificationType = data
    });

    this.control.setValue(this.item.officer.officerType === OfficerTypeEnum.Corporate ?
      OfficerTypeEnum.Corporate : OfficerTypeEnum.Individual);
  }

  protected init(): void {
    this.control.valueChanges
      .pipe(
        startWith(this.item.officer.officerType),
        tap(() => {
          this.form = this.control.value === OfficerTypeEnum.Corporate
            ? this.companyIndirectCompanyShareholderForm
            : this.companyIndirectIndividualShareholderForm

          this.form.controls.percentageOfShares.setValidators([
            Validators.required,
            Validators.biggerThan(0),
            Validators.max(this.item ? this.item.remainingPercentageOfSharesForUpdating : 0)
          ])

          this.form.controls.percentageOfShares.updateValueAndValidity({ emitEvent: false });

          this.setProps()
        })
      ).subscribe()
  }

  protected setProps(): void {
    this.form.patchValue({
      companyId: this.item.id, companyType: this.item.officer.officerType
    })

    if (this.item.officer) {
      const profile = this.item.officer.officerProfile

      if ((this.item instanceof CompanyIndirectShareholder) || (this.item instanceof CompanyShareholder)) {
        this.form.patchValue({ ...this.item, percentageOfShares: this.item.percentageOfShares })
      }


      if (profile instanceof OfficerIndirectCompany) {
        this.form.patchValue({
          officerProfile: {
            ...profile,
            countryId: profile.country.id,
            registrationNumber: profile.registrationNumber,
            registrationDate: new Date(profile.registrationDate)
          }
        })
      }

      if (profile instanceof OfficerIndirectPerson) {
        this.form.patchValue({
          officerProfile: {
            ...profile,
            nationalityId: profile.nationality.id,
            countryId: profile.country.id,
            identificationTypeId: profile.identificationType.id,
          }
        })
      }

      this.form.controls.percentageOfShares.updateValueAndValidity({ emitEvent: false })
    }
  }

  protected api(): Observable<CompanyIndirectShareholder> {
    if (this.item) {
      return this.control.value === OfficerTypeEnum.Corporate
        ? this.companyIndirectShareholderApi.updateIndirectCompanyShareholder({
          id: this.item.id,
          ...this.companyIndirectCompanyShareholderForm.value,
        } as UpdateCompanyIndirectCompanyShareholderInput)
        : this.companyIndirectShareholderApi.updateIndirectIndividualShareholder({
          id: this.item.id,
          ...this.companyIndirectIndividualShareholderForm.value
        } as UpdateCompanyIndirectIndividualShareholderInput)
    }

    return this.control.value === OfficerTypeEnum.Corporate
      ? this.companyIndirectShareholderApi.createIndirectCompanyShareholder(this.companyIndirectCompanyShareholderForm.value as CompanyIndirectCompanyShareholderInput)
      : this.companyIndirectShareholderApi.createIndirectIndividualShareholder(this.companyIndirectIndividualShareholderForm.value as CompanyIndirectIndividualShareholderInput)
  }

  public getRepresentativeInfo(
    officerProfile: OfficerProfile,
  ): OfficerPerson | null {
    if (officerProfile instanceof OfficerCompany) {
      const { representative } = officerProfile;
      if (representative.officerProfile.__typename === 'OfficerPersonType') {
        return representative.officerProfile as OfficerPerson;
      }
    }
    return null;
  }

  onEdit(officerInfo: OfficerInformationItem) {
    if (officerInfo instanceof CompanyIndirectShareholder) {
      return;
    }

    const companyProfile = this.companyEventService.company;

    if (!companyProfile) {
      return;
    }

    const dialog = this.companyEventService.onEditOfficerInformation$(this.matDialog, OfficerResolutionRequestDialogComponent, {
      companyProfile,
      officerInfo
    })

    dialog.componentInstance.officerRequestState.result$.pipe(
      first(),
      tap(item => {
        const updatedOfficerInfo = item as OfficerInformationItem

        this.officerInfo = updatedOfficerInfo
        this.officerUpdated.emit(updatedOfficerInfo)
      }),
    ).subscribe()
  }

  submitForm(): void {
    this.api().pipe(
      switchMap((res) => {
        if (!res) {
          this.snackBarService.pushErrorMessage()

          return EMPTY
        }
        this.officerUpdated.emit(res);

        return EMPTY
      }),
      catchError(e => {
        this.snackBarService.pushErrorMessage()

        return EMPTY
      }),
      finalize(() => {
        this._loading$.next(false)
      })
    ).subscribe(() => {
      this.isVisible = false;
    });

  }

  protected readonly officerType = OfficerTypeEnum;
}
